import { useCallback, useRef, useState } from "react";
import { useAsyncHandler } from "../../../../hooks/useAsyncHandler";
import { retryBatchJobItem, type BatchJob } from "../../../../lib/corpusApi";
import type { BatchJobItemRow } from "./batch_job_item_rows";

export interface UseBatchJobItemRetryOptions {
  job: BatchJob;
  /** Refetches the list once the item is put back in line. */
  refresh: () => Promise<void>;
  /** Refetches the job, which a retry on a completed job reopens. */
  onJobChanged?: () => Promise<void>;
}

export interface UseBatchJobItemRetryResult {
  /** Retries one failed item. */
  handle: (row: BatchJobItemRow<unknown>) => void;
  /** Id of the item a retry is in flight (or last failed) for, if any. */
  retryingId: string | null;
  loading: boolean;
  error: string | null;
}

/** Retries single failed items of a batch job. The item leaves the failed list, and the job is
 *  refetched too, since a retry reopens a completed job. */
export function useBatchJobItemRetry({
  job,
  refresh,
  onJobChanged,
}: UseBatchJobItemRetryOptions): UseBatchJobItemRetryResult {
  const [retryingId, setRetryingId] = useState<string | null>(null);
  const retryingIdRef = useRef<string | null>(null);

  const retry = useAsyncHandler(async () => {
    const id = retryingIdRef.current;
    if (!id) return;
    await retryBatchJobItem(job.id, id);
    await Promise.all([refresh(), onJobChanged?.()]);
  });

  const handle = useCallback(
    (row: BatchJobItemRow<unknown>) => {
      retryingIdRef.current = row.item.id;
      setRetryingId(row.item.id);
      void retry.handle();
    },
    [retry]
  );

  return { handle, retryingId, loading: retry.loading, error: retry.error };
}
